"use client";

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, 
  MessageSquare, 
  Send, 
  User, 
  Mail, 
  Search, 
  Clock,
  Inbox,
  Send as SentIcon,
  X,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

// Helper function to format time (e.g., "5 minutes ago")
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  const months = Math.round(days / 30);
  const years = Math.round(days / 365);

  if (seconds < 60) return `${seconds} seconds ago`;
  if (minutes < 60) return `${minutes} minutes ago`;
  if (hours < 24) return `${hours} hours ago`;
  if (days < 30) return `${days} days ago`;
  if (months < 12) return `${months} months ago`;
  return `${years} years ago`;
};

export default function MessagesPage() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('inbox');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [newMessageForm, setNewMessageForm] = useState({
    recipient: null,
    subject: '',
    body: '',
  });
  const [recipientSearchTerm, setRecipientSearchTerm] = useState('');
  const [filteredRecipients, setFilteredRecipients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const searchInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Fetch all registered users from the backend
  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, {
        credentials: 'include'
      });
      const usersForRecipients = response.data.filter(u => u.id !== user?.id);
      setAllUsers(usersForRecipients);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users for recipient search.');
    }
  };

  // Load messages from localStorage or generate mock data
  const loadMessages = () => {
    setIsLoading(true);
    if (user) {
      try {
        const storedMessages = localStorage.getItem('fortress_messages');
        if (storedMessages) {
          setMessages(JSON.parse(storedMessages));
        } else {
          // Generate initial mock data if none exists
          const mockUsers = [
            { id: 'user-admin', name: 'Admin', surname: 'User', email: 'admin@fortress.com' },
            { id: 'user-prop-manager', name: 'Property', surname: 'Manager', email: 'propman@fortress.com' },
          ];
          const initialMessages = [
            {
              id: 'msg-in-1',
              sender: mockUsers[0],
              recipient: user,
              subject: 'Welcome to Fortress Messaging',
              body: 'Hi, welcome to the new internal messaging system. Feel free to reach out to other users here.',
              timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
              read: false,
            },
            {
              id: 'msg-in-2',
              sender: mockUsers[1],
              recipient: user,
              subject: 'Q3 Asset Report',
              body: 'Please find the Q3 asset report attached to the latest service log. Let me know if you have any questions.',
              timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
              read: true,
            },
          ];
          setMessages(initialMessages);
          localStorage.setItem('fortress_messages', JSON.stringify(initialMessages));
        }
      } catch (e) {
        console.error("Failed to load or parse messages from localStorage", e);
        setMessages([]);
      }
    }
    setIsLoading(false);
  };

  // Handle sending a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessageForm.recipient || !newMessageForm.subject || !newMessageForm.body) {
      toast.error('Please select a recipient and fill in all fields.');
      return;
    }

    const newMessage = {
      id: `msg-new-${Date.now()}`,
      sender: { id: user.id, name: user.name, surname: user.surname, email: user.email },
      recipient: newMessageForm.recipient,
      subject: newMessageForm.subject,
      body: newMessageForm.body,
      timestamp: new Date().toISOString(),
      read: false,
    };

    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    localStorage.setItem('fortress_messages', JSON.stringify(updatedMessages));

    toast.success('Message sent successfully!');
    setShowNewMessageModal(false);
    setNewMessageForm({ recipient: null, subject: '', body: '' });
    setRecipientSearchTerm('');
    setFilteredRecipients([]);
    setActiveTab('sent');
  };

  // Handle viewing a message
  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    if (message.recipient.id === user.id && !message.read) {
      const updatedMessages = messages.map(msg =>
        msg.id === message.id ? { ...msg, read: true } : msg
      );
      setMessages(updatedMessages);
      localStorage.setItem('fortress_messages', JSON.stringify(updatedMessages));
    }
  };

  // Effect to load messages on mount
  useEffect(() => {
    loadMessages();
  }, [user]);

  // Effect to handle recipient search
  useEffect(() => {
    if (recipientSearchTerm) {
      const results = allUsers.filter(
        (u) =>
          u.name.toLowerCase().includes(recipientSearchTerm.toLowerCase()) ||
          u.surname.toLowerCase().includes(recipientSearchTerm.toLowerCase()) ||
          u.email.toLowerCase().includes(recipientSearchTerm.toLowerCase())
      );
      setFilteredRecipients(results);
    } else {
      setFilteredRecipients([]);
    }
  }, [recipientSearchTerm, allUsers]);

  // Effect to fetch users when modal opens
  useEffect(() => {
    if (showNewMessageModal) {
      fetchAllUsers();
    }
  }, [showNewMessageModal]);

  // Filter messages for display
  const inboxMessages = messages.filter(msg => msg.recipient.id === user?.id);
  const sentMessages = messages.filter(msg => msg.sender.id === user?.id);
  const displayedMessages = activeTab === 'inbox' ? inboxMessages : sentMessages;
  const unreadCount = inboxMessages.filter(msg => !msg.read).length;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Not Logged In</h2>
          <p className="text-gray-600">Please log in to view your messages.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <MessageSquare className="mr-3 h-6 w-6 text-cyan-600" />
            My Messages
          </h1>
          <p className="text-gray-600">Your private inbox</p>
        </div>
        
        <button
          onClick={() => setShowNewMessageModal(true)}
          className="bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 flex items-center gap-2"
        >
          <Plus size={16} />
          New Message
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('inbox')}
          className={`flex-1 py-2 px-4 text-sm font-medium text-center transition-colors ${
            activeTab === 'inbox'
              ? 'text-cyan-600 border-b-2 border-cyan-600'
              : 'text-gray-500 hover:text-gray-700'
          } flex items-center justify-center gap-2`}
        >
          <Inbox size={16} />
          Inbox {unreadCount > 0 && `(${unreadCount})`}
        </button>
        <button
          onClick={() => setActiveTab('sent')}
          className={`flex-1 py-2 px-4 text-sm font-medium text-center transition-colors ${
            activeTab === 'sent'
              ? 'text-cyan-600 border-b-2 border-cyan-600'
              : 'text-gray-500 hover:text-gray-700'
          } flex items-center justify-center gap-2`}
        >
          <SentIcon size={16} />
          Sent
        </button>
      </div>

      {/* Message List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {activeTab === 'inbox' ? 'Received Messages' : 'Sent Messages'}
          </h2>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading messages...</p>
          </div>
        ) : displayedMessages.length === 0 ? (
          <div className="text-center py-12">
            {activeTab === 'inbox' ? (
              <Inbox className="mx-auto h-12 w-12 text-gray-400" />
            ) : (
              <SentIcon className="mx-auto h-12 w-12 text-gray-400" />
            )}
            <h3 className="mt-2 text-sm font-medium text-gray-900">No messages yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeTab === 'inbox' ? 'Your inbox is empty.' : 'You haven\'t sent any messages yet.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {displayedMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((message) => (
              <div
                key={message.id}
                onClick={() => handleViewMessage(message)}
                className={`p-4 hover:bg-gray-50 cursor-pointer ${message.recipient.id === user.id && !message.read ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User size={16} className="text-gray-500" />
                      <h3 className="font-semibold text-gray-900">
                        {activeTab === 'inbox' ? `${message.sender.name} ${message.sender.surname}` : `${message.recipient.name} ${message.recipient.surname}`}
                      </h3>
                      {activeTab === 'inbox' && !message.read && (
                        <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs rounded-full">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-gray-800 font-medium mb-1">{message.subject}</p>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {message.body.substring(0, 100)}...
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-xs text-gray-500 ml-4">
                    <Clock size={12} className="inline mr-1" />
                    {formatRelativeTime(message.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">New Message</h2>
              <button
                onClick={() => setShowNewMessageModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSendMessage} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User size={16} className="inline mr-1" />
                  Recipient *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={newMessageForm.recipient ? `${newMessageForm.recipient.name} ${newMessageForm.recipient.surname}` : recipientSearchTerm}
                    onChange={(e) => {
                      setRecipientSearchTerm(e.target.value);
                      setNewMessageForm(prev => ({ ...prev, recipient: null }));
                    }}
                    ref={searchInputRef}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="Search for a user by name or email..."
                    required
                    disabled={!!newMessageForm.recipient}
                  />
                  {newMessageForm.recipient && (
                    <button
                      type="button"
                      onClick={() => {
                        setNewMessageForm(prev => ({ ...prev, recipient: null }));
                        setRecipientSearchTerm('');
                        searchInputRef.current?.focus();
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                
                {recipientSearchTerm && !newMessageForm.recipient && filteredRecipients.length > 0 && (
                  <div className="absolute z-10 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 w-full max-w-md max-h-60 overflow-y-auto">
                    {filteredRecipients.map((rec) => (
                      <div
                        key={rec.id}
                        onClick={() => {
                          setNewMessageForm(prev => ({ ...prev, recipient: rec }));
                          setRecipientSearchTerm('');
                          setFilteredRecipients([]);
                        }}
                        className="p-3 hover:bg-gray-100 cursor-pointer flex items-center space-x-2"
                      >
                        <User size={16} className="text-gray-500" />
                        <span>{rec.name} {rec.surname} ({rec.email})</span>
                      </div>
                    ))}
                  </div>
                )}

                {newMessageForm.recipient && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                    <p className="font-medium text-blue-800">Sending to:</p>
                    <p className="text-blue-700">{newMessageForm.recipient.name} {newMessageForm.recipient.surname} ({newMessageForm.recipient.email})</p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={newMessageForm.subject}
                  onChange={(e) => setNewMessageForm({ ...newMessageForm, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Message subject"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Body *
                </label>
                <textarea
                  value={newMessageForm.body}
                  onChange={(e) => setNewMessageForm({ ...newMessageForm, body: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                  placeholder="Write your message here..."
                  required
                />
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowNewMessageModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors flex items-center gap-2"
                >
                  <Send size={16} />
                  Send Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Message Details</h2>
              <button
                onClick={() => setSelectedMessage(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-500">
                  {selectedMessage.recipient.id === user.id ? 'From:' : 'To:'}
                </p>
                <p className="font-semibold text-gray-900">
                  {selectedMessage.recipient.id === user.id 
                    ? `${selectedMessage.sender.name} ${selectedMessage.sender.surname} (${selectedMessage.sender.email})`
                    : `${selectedMessage.recipient.name} ${selectedMessage.recipient.surname} (${selectedMessage.recipient.email})`
                  }
                </p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Subject:</p>
                <p className="font-semibold text-gray-900">{selectedMessage.subject}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Message:</p>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.body}</p>
              </div>

              <div className="text-xs text-gray-500 flex items-center gap-1">
                <Clock size={12} />
                <span>{new Date(selectedMessage.timestamp).toLocaleString()}</span>
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setSelectedMessage(null)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}